import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _filter from 'lodash/filter';
import _values from 'lodash/values';
import _map from 'lodash/map';
import _toString from 'lodash/toString';
import _isEmpty from 'lodash/isEmpty';
import _includes from 'lodash/includes';
import _reject from 'lodash/reject';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// Core components
import TextField from 'components/core/TextField';
// Models
import mDevice from 'models/device';
import mEquipment from 'models/equipment';
// Custom
import { deviceSlaveTriggerDataShape, deviceSlaveRelayDataShape, deviceSlaveInputDataShape } from 'constants/PropTypesShapes';
import Callout from 'components/misc/Callout';
import ConfigureDeviceSlaveTrigger from './ConfigureDeviceSlaveTrigger';
import ConfigureDeviceSlaveTriggerEthernet from './ConfigureDeviceSlaveTriggerEthernet';
import ConfigureDeviceSlaveRelay from './ConfigureDeviceSlaveRelay';
import ConfigureDeviceSlaveInput from './ConfigureDeviceSlaveInput';

class ConfigureDeviceSlave extends React.Component {
  constructor(props) {
    super(props);

    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleTriggerEquipmentChange = this.handleTriggerEquipmentChange.bind(this);
    this.handleTriggerEthernetChange = this.handleTriggerEthernetChange.bind(this);
    this.handleRelayDoorChange = this.handleRelayDoorChange.bind(this);
    this.handleInputSourceChange = this.handleInputSourceChange.bind(this);
  }

  // Handlers
  handleNameChange(value) {
    const { triggers, relays, inputs } = this.props;

    this.doHandleChange(value, triggers, relays, inputs);
  }

  handleTriggerEquipmentChange(trigger, equipment, idTriggerFallback) {
    const {
      name, triggers, relays, inputs,
    } = this.props;
    const newTriggers = _map(triggers, (t) => (
      t.id === trigger.id ? {
        ...t,
        equipment,
        fallback_trigger_id: idTriggerFallback,
      } : t
    ));

    this.doHandleChange(name, newTriggers, relays, inputs);
  }

  handleTriggerEthernetChange(ethernetTriggers) {
    const { virtualTriggerEthernet } = mDevice;
    const {
      name, triggers, relays, inputs,
    } = this.props;
    // Update triggers: create a trigger ethernet for each equipment
    // Note: must remove previous which were already registered
    const newTriggers = [
      ..._reject(triggers, ['type', virtualTriggerEthernet.type]),
      ...ethernetTriggers,
    ];

    this.doHandleChange(name, newTriggers, relays, inputs);
  }

  handleRelayDoorChange(relay, doors) {
    const {
      name, triggers, relays, inputs,
    } = this.props;
    const newRelays = _map(relays, (r) => (
      r.id === relay.id ? {
        ...r,
        doors,
      } : r
    ));

    this.doHandleChange(name, triggers, newRelays, inputs);
  }

  handleInputSourceChange(input, sources) {
    const {
      name, triggers, relays, inputs,
    } = this.props;
    const newInputs = _map(inputs, (i) => (
      i.id === input.id ? {
        ...i,
        sources,
      } : i
    ));

    this.doHandleChange(name, triggers, relays, newInputs);
  }

  // Private
  doHandleChange(name, triggers, relays, inputs) {
    const { onChange } = this.props;
    onChange({
      name, triggers, relays, inputs,
    });
  }

  render() {
    const {
      isMotherboard, name, triggers, relays, inputs, virtualTriggerTypes, errors,
    } = this.props;

    const protocolTriggersArray = mDevice.sortTriggers(_filter(triggers, (t) => (t.protocols && t.protocols.length > 0 && !_includes(virtualTriggerTypes, t.type))));
    const relaysArray = mDevice.sortRelays(_values(relays));
    const inputsArray = mDevice.sortInputs(_values(inputs));

    const { virtualTriggerEthernet } = mDevice;
    const withEthernetTrigger = _includes(virtualTriggerTypes, virtualTriggerEthernet.type);
    const ethernetTriggers = _filter(triggers, ['type', virtualTriggerEthernet.type]);
    const triggersForFallback = _map(ethernetTriggers, (trigger) => mEquipment.isUsableForOnDemand(trigger.equipment) && trigger);

    return (
      <div>
        {!isMotherboard && (
          <div className="row pb-3">
            <div className="col-12 col-md-6">
              <TextField
                label={<LocalizedMessage id="field.name" />}
                errorText={_get(errors, 'name')}
                value={name}
                required
                onChange={this.handleNameChange}
              />
            </div>
          </div>
        )}
        {!!protocolTriggersArray && !_isEmpty(protocolTriggersArray) && (
          <div>
            <Callout>
              <LocalizedMessage id="subtitle.equipments" />
            </Callout>
            <div className="row">
              {_map(protocolTriggersArray, (trigger) => (
                <div key={_toString(trigger.id)} className="col-xl-3 col-sm-6 col-xs-12 pt-3">
                  <ConfigureDeviceSlaveTrigger
                    trigger={trigger}
                    triggersForFallback={triggersForFallback}
                    onTriggerEquipmentChange={this.handleTriggerEquipmentChange}
                  />
                </div>
              ))}
              {withEthernetTrigger && (
                <div className="col-12 pt-3">
                  <ConfigureDeviceSlaveTriggerEthernet
                    triggers={ethernetTriggers}
                    onTriggerChange={this.handleTriggerEthernetChange}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {!!relaysArray && !_isEmpty(relaysArray) && (
          <div className="pt-4">
            <Callout>
              <LocalizedMessage id="subtitle.relays" />
            </Callout>
            <div className="row">
              {_map(relaysArray, (relay) => (
                <div key={_toString(relay.id)} className="col-sm-6 col-xs-12 pt-3">
                  <ConfigureDeviceSlaveRelay
                    relay={relay}
                    onRelayDoorChange={this.handleRelayDoorChange}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {!!inputsArray && !_isEmpty(inputsArray) && (
          <div className="pt-4">
            <Callout>
              <LocalizedMessage id="subtitle.inputs" />
            </Callout>
            <div className="row">
              {_map(inputsArray, (input) => (
                <div key={_toString(input.id)} className="col-sm-6 col-xs-12 pt-3">
                  <ConfigureDeviceSlaveInput
                    input={input}
                    onInputSourceChange={this.handleInputSourceChange}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}

ConfigureDeviceSlave.propTypes = {
  isMotherboard: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  triggers: PropTypes.arrayOf(deviceSlaveTriggerDataShape),
  relays: PropTypes.arrayOf(deviceSlaveRelayDataShape),
  inputs: PropTypes.arrayOf(deviceSlaveInputDataShape),
  virtualTriggerTypes: PropTypes.arrayOf(PropTypes.string),
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

ConfigureDeviceSlave.defaultProps = {
  triggers: null,
  relays: null,
  inputs: null,
  virtualTriggerTypes: [],
  errors: null,
};

module.exports = ConfigureDeviceSlave;



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/configuration/ConfigureDeviceSlave.js